export const AFRIGIS_TOKEN_URL = 'https://auth.afrigis.services/oauth2/token';

export type IngestAuthType = 'oauth2' | 'api_key' | 'bearer' | 'basic' | 'hmac' | 'none' | string;

export type OauthGrantType = 'client_credentials' | 'authorization_code';

export type CredentialPresence = {
  auth_type?: string | null;
  grant_type?: string | null;
  has_api_key?: boolean;
  has_access_token?: boolean;
  has_refresh_token?: boolean;
  has_client_id?: boolean;
  has_client_secret?: boolean;
  has_token_url?: boolean;
  has_authorization_url?: boolean;
  has_vault_path?: boolean;
  can_client_credentials?: boolean;
  missing_reason?: string | null;
  vault_path?: string | null;
  api_key_in?: string | null;
  api_key_param?: string | null;
  api_key_length?: number | null;
  api_key_last4?: string | null;
  inherited_from_api_id?: string | null;
  vault_inherit?: string | null;
};

export type CredentialFormState = {
  api_key: string;
  client_id: string;
  client_secret: string;
  token_url: string;
  authorization_url: string;
  grant_type: OauthGrantType;
  x_api_key: string;
  scopes: string;
  provider: string;
};

const REDACTED = '[REDACTED]';

export function looksLikeAfrigis(provider?: string | null): boolean {
  return String(provider || '').toLowerCase().replace(/[^a-z0-9]+/g, '').includes('afrigis');
}

export function resolveOauthGrantType(input: {
  authType?: string | null;
  grantType?: string | null;
  provider?: string | null;
  tokenUrl?: string | null;
  authorizationUrl?: string | null;
}): OauthGrantType {
  const grant = String(input.grantType || '').trim().toLowerCase();
  if (grant === 'client_credentials') return 'client_credentials';
  if (grant === 'authorization_code') return 'authorization_code';
  if (looksLikeAfrigis(input.provider)) return 'client_credentials';
  if (input.tokenUrl && !input.authorizationUrl) return 'client_credentials';
  return 'authorization_code';
}

export function defaultTokenUrl(provider?: string | null, existing?: string | null): string {
  if (existing && String(existing).trim()) return String(existing).trim();
  if (looksLikeAfrigis(provider)) return AFRIGIS_TOKEN_URL;
  return '';
}

export function emptyCredentialForm(input: {
  authType?: string | null;
  provider?: string | null;
  grantType?: string | null;
  tokenUrl?: string | null;
  authorizationUrl?: string | null;
  clientId?: string | null;
} = {}): CredentialFormState {
  const grantType = resolveOauthGrantType(input);
  return {
    api_key: '',
    client_id: input.clientId && input.clientId !== '[REDACTED]' ? String(input.clientId) : '',
    client_secret: '',
    token_url: defaultTokenUrl(input.provider, input.tokenUrl),
    authorization_url: input.authorizationUrl || '',
    grant_type: grantType,
    x_api_key: '',
    scopes: '',
    provider: input.provider || '',
  };
}

export function presentSecretPreview(raw: string | null | undefined, stored: boolean): string {
  const value = String(raw || '').trim();
  if (value && value !== REDACTED) {
    if (value.length <= 4) return '••••';
    return `••••${value.slice(-4)}`;
  }
  return stored ? 'stored' : 'not stored';
}

export function credentialStatusLabel(presence?: CredentialPresence | null, authType?: string | null): string {
  if (!presence) return 'No credentials stored.';
  if (presence.missing_reason) return 'No usable credentials stored.';
  if (authType === 'api_key' && presence.has_api_key) return 'API key stored.';
  if (authType === 'oauth2' && presence.can_client_credentials) {
    return presence.has_api_key
      ? 'Client credentials + API key stored.'
      : 'Client credentials stored.';
  }
  if (presence.has_access_token || presence.has_api_key || presence.has_client_secret) {
    return 'Credentials stored.';
  }
  return 'No credentials stored.';
}

export function buildApiKeySavePayload(form: Pick<CredentialFormState, 'api_key'>): { auth_type: 'api_key'; api_key: string } {
  return {
    auth_type: 'api_key',
    api_key: form.api_key.trim(),
  };
}

export function buildOauthClientCredentialsSavePayload(form: CredentialFormState): {
  auth_type: 'oauth2';
  grant_type: 'client_credentials';
  client_id: string;
  client_secret: string;
  token_url?: string;
  x_api_key?: string;
  provider?: string;
} {
  const payload: ReturnType<typeof buildOauthClientCredentialsSavePayload> = {
    auth_type: 'oauth2',
    grant_type: 'client_credentials',
    client_id: form.client_id.trim(),
    client_secret: form.client_secret.trim(),
  };
  if (form.token_url.trim()) payload.token_url = form.token_url.trim();
  if (form.x_api_key.trim()) payload.x_api_key = form.x_api_key.trim();
  if (form.provider.trim()) payload.provider = form.provider.trim();
  return payload;
}

/**
 * Browser redirect target for authorization_code grants. Must be outside Kong's
 * JWT-gated /cp/* routes: the provider redirect carries no DevCockpit token.
 * Kong public_oauth_callback_route -> orchestrator public-oauth.js -> api-manager
 * GET /api/oauth/callback (state-verified, no JWT). One stable URL for every
 * provider so it only has to be registered once per provider app.
 */
export const INGEST_OAUTH_CALLBACK_PATH = '/public/oauth/api-ingest/callback';

export function buildIngestOauthRedirectUri(origin: string): string {
  return `${String(origin || '').replace(/\/$/, '')}${INGEST_OAUTH_CALLBACK_PATH}`;
}

/**
 * Character-exact redirect the Setup card should print so a developer can
 * register it at the provider before Connect. Null only when this card does
 * not use a browser callback, or origin is not known yet (honest empty).
 */
export function ingestOauthRedirectUriForCard(input: {
  authType?: string | null;
  grantType?: string | null;
  origin?: string | null;
}): string | null {
  if (String(input.authType || '').toLowerCase() !== 'oauth2') return null;
  if (resolveOauthGrantType({
    authType: input.authType,
    grantType: input.grantType,
  }) !== 'authorization_code') {
    return null;
  }
  const origin = String(input.origin || '').trim();
  if (!origin) return null;
  return buildIngestOauthRedirectUri(origin);
}

export function canSaveWithoutAuthorizeUrl(
  form: CredentialFormState,
  authType?: string | null,
  presence?: CredentialPresence | null,
): boolean {
  if (authType === 'api_key') return Boolean(form.api_key.trim() || presence?.has_api_key);
  if (authType !== 'oauth2') return false;
  const hasClient = Boolean(form.client_id.trim() || presence?.has_client_id);
  const hasSecret = Boolean(form.client_secret.trim() || presence?.has_client_secret);
  return hasClient && hasSecret;
}

export type OauthPollSnapshot = {
  status?: string | null;
  updated_at?: string | null;
  has_access_token?: boolean | null;
};

/**
 * True once the authorize round-trip has visibly landed. Comparing against the
 * pre-connect snapshot (not just "status is no longer pending") keeps the poll
 * running when re-authorising an API that was already active.
 */
export function oauthPollSettled(before: OauthPollSnapshot, after: OauthPollSnapshot): boolean {
  const pending = (st?: string | null) => st === 'pending_authentication' || st === 'pending_oauth';
  if (after.has_access_token && !before.has_access_token) return true;
  if (after.updated_at && after.updated_at !== before.updated_at && !pending(after.status)) return true;
  if (pending(before.status) && after.status && !pending(after.status)) return true;
  return false;
}
